import React, { useContext, useState } from 'react';
import { isTestEnvironment } from '../utils/environment.mjs';
import { AuthContext } from '../authProvider.mjs';
import userManagementService from '../userManagement/service/userManagementService.mjs';
import { useDialog } from './dialog/DialogProvider.mjs';

const ROLES = ['user', 'admin', 'auditor', 'regional_director', 'global_director', 'finance_director'];

const panelStyle = {
  position: 'fixed',
  // Beside the TEST tag (TestEnvironmentBanner, bottom-left), not on top of it.
  bottom: 'max(12px, env(safe-area-inset-bottom))',
  left: '72px',
  zIndex: 2147483647,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: '#1f2937',
  color: '#ffffff',
  padding: '4px 6px 4px 12px',
  borderRadius: '9999px',
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
};

const selectStyle = {
  backgroundColor: '#ffffff',
  color: '#111827',
  border: 'none',
  borderRadius: '6px',
  padding: '3px 6px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
};

/**
 * TEST-ENVIRONMENT ONLY control. Lets a single tester switch their own role so
 * they can walk every module end to end (submit → admin → auditor → director).
 * Renders only in test mode for an authenticated user; the backend endpoint is
 * hard-gated to APP_ENVIRONMENT=test, so this never affects production.
 *
 * variant="floating" (default): the pill pinned bottom-left, from md up. Below md
 *   it is hidden (index.css) - on a phone it sat over the bottom buttons.
 * variant="menu": an inline row for the navbar's phone menu panel.
 */
export default function TestRoleSwitcher({ variant = 'floating' }) {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  // Declared before the early return below: hooks cannot be called conditionally.
  const { alert } = useDialog();

  // Shown only when the SERVER says this account may switch. isTestEnvironment()
  // alone cannot tell: FORCE_TEST_ENVIRONMENT makes it true for every visitor,
  // so it would keep showing the switcher after the backend leaves test mode.
  // The server enforces the same rule regardless.
  if (!isTestEnvironment() || !isAuthenticated || !user || user.canSwitchTestRole !== true) {
    return null;
  }

  const onChange = async (e) => {
    const role = e.target.value;
    if (!role || role === user.role) return;
    setBusy(true);
    try {
      await userManagementService.setTestRole(role);
      // Reload so auth state, navigation and permissions reflect the new role.
      window.location.reload();
    } catch (err) {
      setBusy(false);
      alert('Could not switch role: ' + err.message, { tone: 'danger' });
    }
  };

  if (variant === 'menu') {
    return (
      <div
        className="flex items-center justify-between gap-3 mt-2 pt-3 px-3 border-t border-gray-100"
        role="group"
        aria-label="Test role switcher"
        data-testid="test-role-switcher-menu"
      >
        <span className="text-sm font-medium text-gray-600">Test role</span>
        <select
          className="min-h-[44px] flex-1 max-w-[60%] px-3 border border-gray-300 rounded-lg bg-white text-gray-800 disabled:opacity-50"
          value={user.role || 'user'}
          onChange={onChange}
          disabled={busy}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div style={panelStyle} className="test-chrome-floating" role="group" aria-label="Test role switcher" data-testid="test-role-switcher">
      <span>Test role:</span>
      <select style={selectStyle} value={user.role || 'user'} onChange={onChange} disabled={busy}>
        {ROLES.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
    </div>
  );
}
